import { Award, Leaf, Users, Shield, Quote } from 'lucide-react';
import { motion } from 'framer-motion';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const ValuesSection = () => {
  const values = [
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Authenticity',
      description: 'Every product is genuine, made using traditional techniques passed down through generations.',
      bar: 'from-clay-500 to-clay-600',
      chip: 'from-clay-100 to-clay-200',
      iconColor: 'text-clay-600',
    },
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Sustainability',
      description: 'We use eco-friendly materials and promote sustainable practices in all our operations.',
      bar: 'from-green-500 to-green-600',
      chip: 'from-green-100 to-green-200',
      iconColor: 'text-green-700',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Fair Trade',
      description: 'Artisans receive fair compensation for their work, ensuring sustainable livelihoods.',
      bar: 'from-saffron-500 to-saffron-600',
      chip: 'from-saffron-100 to-saffron-200',
      iconColor: 'text-saffron-700',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Quality',
      description: 'Rigorous quality checks ensure every product meets our high standards before reaching you.',
      bar: 'from-cocoa-500 to-cocoa-700',
      chip: 'from-cream-200 to-cocoa-200',
      iconColor: 'text-cocoa-700',
    },
  ];

  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeInUp}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-cocoa-900">Our Core Values</h2>
          <p className="text-cocoa-600 max-w-2xl mx-auto text-lg">
            The principles that guide everything we do at তন্তিকা
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.08 }}
              className="group relative overflow-hidden bg-white rounded-2xl border border-cream-200 shadow-sm hover:shadow-warm hover:-translate-y-1 transition-all duration-300"
            >
              {/* Top Gradient Bar */}
              <div className={`h-2 bg-gradient-to-r ${value.bar}`}></div>

              <div className="p-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${value.chip} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className={value.iconColor}>{value.icon}</div>
                </div>

                <h3 className="text-xl font-bold text-cocoa-900 mb-4">{value.title}</h3>
                <p className="text-cocoa-600">{value.description}</p>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-transparent to-cream-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="inline-block bg-gradient-to-br from-clay-50 to-saffron-50 rounded-3xl p-8 md:p-10 max-w-3xl mx-auto border border-cream-200">
            <Quote className="w-8 h-8 text-clay-400 mx-auto mb-4" />
            <p className="text-lg md:text-xl text-cocoa-800 font-medium leading-relaxed">
              "At তন্তিকা, we believe that preserving cultural heritage and supporting artisans
              isn't just good business - it's our responsibility to future generations."
            </p>
            <div className="mt-4 text-cocoa-600 font-semibold">
              — The তন্তিকা Team
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ValuesSection;
